import { Stethoscope, Smile, Users, GraduationCap, Building2, Landmark, ArrowRight, Bot, CalendarCheck, CheckCircle2, TrendingUp } from "lucide-react";
import { Reveal, SectionHeading } from "./shared";

function CopilotVisual() {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-md bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center shrink-0">
          <Bot className="w-3 h-3 text-white" strokeWidth={2} />
        </span>
        <div className="flex-1 space-y-1">
          <div className="h-1.5 w-4/5 rounded-full bg-slate-200" />
          <div className="h-1.5 w-3/5 rounded-full bg-slate-200" />
        </div>
      </div>
      <div className="ml-auto h-4 w-2/3 rounded-lg rounded-br-sm bg-[#2563EB]/15" />
      <div className="h-4 w-5/6 rounded-lg rounded-bl-sm bg-slate-200/70" />
      <div className="h-5 rounded-full border border-slate-200 bg-white flex items-center px-2.5">
        <span className="h-1.5 w-1/3 rounded-full bg-slate-200" />
      </div>
    </div>
  );
}

function PatientVisual() {
  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-gradient-to-r from-[#2563EB] to-[#6366F1] p-2.5">
        <div className="flex items-center justify-between">
          <span className="h-1.5 w-12 rounded-full bg-white/50" />
          <span className="text-[9px] font-bold text-white">72%</span>
        </div>
        <div className="h-1 rounded-full bg-white/25 mt-1.5">
          <div className="h-full w-[72%] rounded-full bg-white" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" strokeWidth={2} />
        </span>
        <div className="h-1.5 flex-1 rounded-full bg-slate-200" />
      </div>
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-md bg-[#6366F1]/10 flex items-center justify-center shrink-0">
          <Smile className="w-3 h-3 text-[#6366F1]" strokeWidth={2} />
        </span>
        <div className="h-1.5 flex-1 rounded-full bg-slate-200" />
      </div>
    </div>
  );
}

function ParentVisual() {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2.5 rounded-lg bg-white border border-slate-200/70 p-2.5">
        <span className="w-7 h-7 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
          <CalendarCheck className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-[#0F172A] leading-tight">Aligner check</p>
          <p className="text-[9px] text-slate-400">Thu, Aug 6 · 09:00</p>
        </div>
        <span className="text-[8px] font-bold text-emerald-600 bg-emerald-500/10 rounded-full px-1.5 py-0.5 shrink-0">Confirmed</span>
      </div>
      <div className="flex items-center gap-2.5 rounded-lg bg-white border border-slate-200/70 p-2.5">
        <span className="w-7 h-7 rounded-lg bg-[#6366F1]/10 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#6366F1]" strokeWidth={2} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-[#0F172A] leading-tight">Milestone reached</p>
          <p className="text-[9px] text-slate-400">Tray 18 of 25 complete</p>
        </div>
      </div>
    </div>
  );
}

function UniversityVisual() {
  const rows = [
    { initials: "RK", status: "Approved", ok: true },
    { initials: "AM", status: "In review", ok: false },
    { initials: "JT", status: "Approved", ok: true },
  ];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Resident case review</span>
        <span className="text-[8px] font-bold text-[#2563EB] bg-[#2563EB]/10 rounded-full px-1.5 py-0.5">12 active</span>
      </div>
      {rows.map((row) => (
        <div key={row.initials} className="flex items-center gap-2 rounded-lg bg-white border border-slate-200/70 px-2 py-1.5">
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#2563EB]/80 to-[#6366F1]/80 flex items-center justify-center text-[7px] font-bold text-white shrink-0">
            {row.initials}
          </span>
          <div className="h-1.5 flex-1 rounded-full bg-slate-200" />
          <span className={`text-[8px] font-bold rounded-full px-1.5 py-0.5 shrink-0 ${row.ok ? "text-emerald-600 bg-emerald-500/10" : "text-amber-600 bg-amber-500/10"}`}>
            {row.status}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChainVisual() {
  return (
    <div>
      <div className="flex items-end gap-1.5 h-14">
        {[45, 70, 55, 90, 65, 100].map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-md bg-gradient-to-t from-[#2563EB]/80 to-[#6366F1]/80 transition-all duration-500"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1.5">
        {["Downtown", "West", "North"].map((loc) => (
          <span key={loc} className="text-[8px] font-medium text-slate-400">{loc}</span>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <TrendingUp className="w-3 h-3 text-emerald-500" strokeWidth={2} />
        <span className="text-[9px] font-semibold text-emerald-600">+18% efficiency across 3 clinics</span>
      </div>
    </div>
  );
}

function OrgVisual() {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { value: "98.2%", label: "Quality" },
          { value: "41h", label: "Saved" },
          { value: "A+", label: "Audit" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg bg-white border border-slate-200/70 px-1.5 py-2 text-center">
            <p className="text-[11px] font-bold text-[#0F172A] leading-none">{kpi.value}</p>
            <p className="text-[8px] text-slate-400 mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>
      <svg viewBox="0 0 200 32" className="w-full">
        <defs>
          <linearGradient id="orgSpark" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
        <path d="M0 26 C 30 22, 50 26, 75 16 S 120 12, 150 8 S 185 4, 200 3" fill="none" stroke="url(#orgSpark)" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

const AUDIENCES = [
  { icon: Stethoscope, title: "For Orthodontists", slug: "orthodontists", copy: "Spend less time documenting visits and managing administrative work, and more time delivering exceptional patient care with your AI clinical copilot.", tint: "bg-[#2563EB]/10 text-[#2563EB]", visual: CopilotVisual },
  { icon: Smile, title: "For Patients", slug: "patients", copy: "Stay engaged throughout treatment with smart reminders, progress tracking, AI guidance, and seamless communication with your orthodontic team.", tint: "bg-[#6366F1]/10 text-[#6366F1]", visual: PatientVisual },
  { icon: Users, title: "For Parents & Families", slug: "parents-families", copy: "Monitor appointments, treatment milestones, treatment progress, and important updates without needing to contact the clinic.", tint: "bg-sky-500/10 text-sky-600", visual: ParentVisual },
  { icon: GraduationCap, title: "For Dental Universities", slug: "dental-universities", copy: "Empower students and faculty with AI-assisted case management, clinical documentation, and digital orthodontic education.", tint: "bg-violet-500/10 text-violet-600", visual: UniversityVisual },
  { icon: Building2, title: "For Dental Groups & Chains", slug: "dental-groups-chains", copy: "Standardize workflows, monitor clinic performance, and manage multiple locations through one intelligent AI platform.", tint: "bg-emerald-500/10 text-emerald-600", visual: ChainVisual },
  { icon: Landmark, title: "For Healthcare Organizations", slug: "healthcare-organizations", copy: "Support digital transformation, quality improvement, clinical reporting, and AI-powered healthcare workflows across hospitals and healthcare networks.", tint: "bg-amber-500/10 text-amber-600", visual: OrgVisual },
];

export default function WhoWeServe() {
  return (
    <section id="solutions" data-testid="solutions-section" className="relative py-24 lg:py-32 bg-[#F8FAFC] overflow-hidden">
      <div className="absolute -bottom-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#2563EB]/8 blur-[100px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <SectionHeading
          testId="solutions-heading"
          eyebrow="Solutions"
          title="One AI Platform. Every Orthodontic Workflow."
          sub="Whether you're treating patients, managing multiple clinics, training future orthodontists, or improving patient engagement, Alignexa AI adapts to your workflow and helps every stakeholder work smarter."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {AUDIENCES.map((aud, i) => (
            <Reveal key={aud.slug} delay={(i % 3) * 0.1} className="h-full">
              <div
                data-testid={`solutions-card-${aud.slug}`}
                className="group relative h-full rounded-3xl border border-slate-200/80 bg-white p-8 flex flex-col transition-[box-shadow,transform,border-color] duration-500 hover:-translate-y-2 hover:shadow-[0_32px_70px_-15px_rgba(37,99,235,0.28)] hover:border-[#2563EB]/30"
              >
                <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#2563EB]/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className={`w-14 h-14 rounded-2xl ${aud.tint} flex items-center justify-center transition-[transform,background-color,color,box-shadow] duration-500 group-hover:scale-110 group-hover:-rotate-3 group-hover:bg-gradient-to-br group-hover:from-[#2563EB] group-hover:to-[#6366F1] group-hover:text-white group-hover:shadow-[0_12px_28px_rgba(37,99,235,0.35)]`}>
                  <aud.icon className="w-6 h-6" strokeWidth={1.7} />
                </div>
                <h3 className="font-display font-medium text-xl tracking-tight text-[#0F172A] mt-6">
                  {aud.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-2.5">{aud.copy}</p>

                <div className="mt-6 rounded-2xl border border-slate-200/70 bg-[#F8FAFC] p-3.5 h-[132px] overflow-hidden transition-[background-color,border-color,transform,box-shadow] duration-500 group-hover:bg-white group-hover:border-[#2563EB]/25 group-hover:scale-[1.02] group-hover:shadow-[0_12px_28px_rgba(37,99,235,0.12)]">
                  <aud.visual />
                </div>

                <button
                  type="button"
                  data-testid={`solutions-learn-more-${aud.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] mt-auto pt-6 transition-colors duration-200 hover:text-[#1D4ED8]"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2} />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <p data-testid="solutions-vision-line" className="text-center text-sm text-slate-400 mt-16 max-w-xl mx-auto leading-relaxed">
            Orthodontics is just the beginning. Alignexa AI is becoming the{" "}
            <span className="font-semibold text-[#0F172A]">AI operating system for dentistry</span>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
